<template>
  <div class="public-shop">
    <header class="shop-header" :style="{ background: themeGradient }">
      <div class="container py-3">
        <router-link :to="shopPath('')" class="header-link">
          <i class="fas fa-arrow-left"></i> Kembali ke Toko
        </router-link>
      </div>
    </header>

    <div class="container mt-4 pb-5">
      <div v-if="loading" class="shop-card p-5 text-center">
        <div class="spinner-border text-primary"></div>
        <p class="text-muted mt-3 mb-0">Memuat status pesanan...</p>
      </div>

      <template v-else-if="order">
        <div class="text-center mb-4 status-hero">
          <div v-if="isPaid" class="text-success">
            <i class="fas fa-check-circle status-icon"></i>
            <h3>Pembayaran Berhasil!</h3>
            <p class="text-muted">Terima kasih, pesanan Anda akan segera diproses.</p>
          </div>
          <div v-else-if="order.status === 'cancelled'">
            <i class="fas fa-times-circle text-danger status-icon"></i>
            <h3>Pesanan Dibatalkan</h3>
            <p class="text-muted">Pesanan ini sudah tidak aktif.</p>
          </div>
          <div v-else>
            <i class="fas fa-clock text-warning status-icon"></i>
            <h3>Menunggu Pembayaran</h3>
            <p class="text-muted">Silakan selesaikan pembayaran untuk diproses.</p>
          </div>
        </div>

        <div class="shop-card p-4 mb-3">
          <h5 class="mb-3"><i class="fas fa-receipt text-primary"></i> Detail Pesanan</h5>
          <div class="row detail-row">
            <div class="col-sm-4 text-muted">Invoice</div>
            <div class="col-sm-8">
              <code>{{ order.invoice_number || order.order_id }}</code>
              <button class="btn btn-sm btn-link p-0 ml-2" @click="copyText(order.invoice_number || order.order_id)">
                <i class="fas fa-copy"></i>
              </button>
            </div>
          </div>
          <div class="row detail-row" v-if="order.customer_name">
            <div class="col-sm-4 text-muted">Customer</div>
            <div class="col-sm-8">{{ order.customer_name }}</div>
          </div>
          <div class="row detail-row">
            <div class="col-sm-4 text-muted">Status Pembayaran</div>
            <div class="col-sm-8">
              <span class="badge" :class="paymentBadgeClass">{{ paymentStatusLabel }}</span>
            </div>
          </div>
          <div class="row detail-row">
            <div class="col-sm-4 text-muted">Status Pesanan</div>
            <div class="col-sm-8">
              <span class="badge" :class="orderBadgeClass">{{ orderStatusLabel }}</span>
            </div>
          </div>
          <div class="row detail-row" v-if="paymentMethodLabel">
            <div class="col-sm-4 text-muted">Metode Bayar</div>
            <div class="col-sm-8">{{ paymentMethodLabel }}</div>
          </div>

          <div class="mt-3 pt-3 border-top">
            <div v-for="it in items" :key="it.id" class="d-flex justify-content-between mb-2 small">
              <span>{{ it.product_name }} × {{ it.quantity }}</span>
              <span>{{ formatRupiah(it.subtotal) }}</span>
            </div>
            <div class="d-flex justify-content-between mb-1 small">
              <span class="text-muted">Subtotal</span>
              <span>{{ formatRupiah(subtotal) }}</span>
            </div>
            <div class="d-flex justify-content-between mb-2 small">
              <span class="text-muted">Ongkir</span>
              <span>{{ formatRupiah(order.shipping_cost) }}</span>
            </div>
            <div class="d-flex justify-content-between">
              <b>Total</b>
              <b class="text-primary">{{ formatRupiah(order.total_amount) }}</b>
            </div>
          </div>
        </div>

        <div v-if="isPaid && hasDigitalAccess" class="shop-card p-4 mb-3">
          <h5 class="mb-3"><i class="fas fa-gift text-primary"></i> Akses Produk Digital</h5>
          <div v-for="item in digitalAccessItems" :key="item.product_id" class="digital-access-item mb-3">
            <div class="font-weight-bold mb-2">{{ item.product_name }}</div>
            <pre v-if="item.access_text" class="access-text-box">{{ item.access_text }}</pre>
            <p v-else class="text-muted small mb-0">Akses produk akan dikirim ke WhatsApp &amp; email Anda.</p>
          </div>
          <p class="text-muted small mt-3 mb-0">
            <i class="fas fa-info-circle"></i> Link akses juga dikirim ke WhatsApp &amp; email Anda.
          </p>
        </div>

        <div v-if="isPending" class="shop-card p-4 mb-3">
          <h5 class="mb-3"><i class="fas fa-credit-card text-primary"></i> Lanjutkan Pembayaran</h5>

          <div v-if="isManualTransfer">
            <p class="mb-2">Transfer ke rekening berikut:</p>
            <div class="bank-box" v-if="bankAccount">
              <pre class="mb-0">{{ bankAccount }}</pre>
              <button class="btn btn-sm btn-outline-secondary mt-2" @click="copyText(bankAccount)">
                <i class="fas fa-copy"></i> Salin Rekening
              </button>
            </div>
            <p v-else class="text-muted">Hubungi admin toko untuk detail rekening transfer.</p>
            <a v-if="waBuktiLink" :href="waBuktiLink" target="_blank" class="btn btn-success btn-block mt-3">
              <i class="fab fa-whatsapp"></i> Kirim Bukti Transfer via WA
            </a>
          </div>

          <div v-else-if="isCod">
            <div class="alert alert-info mb-0">
              <i class="fas fa-truck"></i>
              Pesanan COD — bayar saat barang sampai. Admin akan memproses pesanan Anda.
            </div>
          </div>

          <div v-else>
            <div v-if="tripayPaymentName" class="alert alert-light border mb-3">
              <small class="text-muted d-block">Metode</small>
              <b>{{ tripayPaymentName }}</b>
            </div>

            <div v-if="payCode" class="pay-code-box mb-3">
              <small class="text-muted d-block mb-1">{{ payCodeLabel }}</small>
              <div class="d-flex align-items-center">
                <code class="pay-code flex-grow-1">{{ payCode }}</code>
                <button class="btn btn-outline-primary btn-sm ml-2" @click="copyText(payCode)">
                  <i class="fas fa-copy"></i> Salin
                </button>
              </div>
            </div>

            <div v-if="showQr" class="text-center mb-3">
              <img :src="qrImageUrl" alt="QR Code Pembayaran" class="qr-image" />
              <p class="small text-muted mt-2">Scan QR code di atas untuk bayar</p>
            </div>

            <a
              v-if="paymentUrl"
              :href="paymentUrl"
              target="_blank"
              class="btn btn-primary btn-lg btn-block mb-2"
            >
              💳 Bayar Sekarang
            </a>
            <button
              v-else
              class="btn btn-primary btn-lg btn-block mb-2"
              :disabled="fetchingPayment"
              @click="getPaymentLink"
            >
              <span v-if="fetchingPayment"><i class="fas fa-spinner fa-spin"></i> Memproses...</span>
              <span v-else>💳 Dapatkan Link Bayar</span>
            </button>

            <p v-if="expiredLabel" class="small text-muted text-center mb-0">
              <i class="fas fa-hourglass-half"></i> {{ expiredLabel }}
            </p>
          </div>
        </div>

        <div class="text-center">
          <router-link :to="shopPath('')" class="btn btn-light">Kembali ke Toko</router-link>
        </div>

        <div v-if="isPending" class="text-center text-muted small mt-3">
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': polling }"></i>
          Halaman ini auto-refresh setiap 5 detik untuk update status.
        </div>
      </template>

      <div v-else class="empty-state">
        <i class="fas fa-exclamation-circle empty-state-icon text-danger"></i>
        <h4>Pesanan tidak ditemukan</h4>
        <p class="text-muted">Nomor pesanan mungkin salah atau sudah kadaluarsa.</p>
        <router-link :to="shopPath('')" class="btn btn-primary mt-2">Kembali ke Toko</router-link>
      </div>
    </div>
  </div>
</template>

<script>
import publicShopPaths from './public_shop_paths';
import { applyShopTheme, shopHeaderBackground, pickShopTheme, shopPublicMixin } from '@/components/shop_public/shop_public_utils';
import { trackPurchase } from '@/components/shop_public/shop_tracking';
import '@/components/shop_public/shop_public.css';

export default {
  mixins: [publicShopPaths, shopPublicMixin],
  data() {
    return {
      loading: true,
      polling: false,
      fetchingPayment: false,
      orderId: '',
      order: null,
      items: [],
      shopInfo: null,
      tripay: null,
      digitalAccessItems: [],
      hasDigitalAccess: false,
      pollTimer: null,
      wasPaidAnnounced: false,
    };
  },
  computed: {
    themeGradient() {
      return shopHeaderBackground(pickShopTheme(this.shopInfo, this.$store.state.public_shop.shop));
    },
    bankAccount() {
      return this.shopInfo?.bank_account || this.$store.state.public_shop.shop?.bank_account || '';
    },
    ownerPhone() {
      return this.shopInfo?.owner_phone || this.$store.state.public_shop.shop?.owner_phone || '';
    },
    isPaid() {
      return this.order?.payment_status === 'paid';
    },
    isPending() {
      return this.order?.payment_status === 'pending';
    },
    isManualTransfer() {
      return this.order?.payment_method === 'transfer_manual';
    },
    isCod() {
      return this.order?.payment_method === 'cod';
    },
    subtotal() {
      return this.items.reduce((s, i) => s + (i.subtotal || 0), 0);
    },
    paymentUrl() {
      return this.order?.payment_url || this.tripay?.checkout_url || this.tripay?.pay_url || '';
    },
    payCode() {
      return this.tripay?.pay_code || '';
    },
    tripayPaymentName() {
      return this.tripay?.payment_name || '';
    },
    showQr() {
      const pm = (this.order?.payment_method || '').toUpperCase();
      const name = (this.tripay?.payment_name || '').toUpperCase();
      return this.payCode && (pm.includes('QRIS') || name.includes('QRIS'));
    },
    qrImageUrl() {
      if (!this.payCode) return '';
      return 'https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=' + encodeURIComponent(this.payCode);
    },
    payCodeLabel() {
      const name = (this.tripay?.payment_name || '').toUpperCase();
      if (name.includes('QRIS')) return 'QR Code';
      if (name.includes('VA') || name.includes('VIRTUAL')) return 'Nomor Virtual Account';
      return 'Kode Pembayaran';
    },
    paymentStatusLabel() {
      const map = {
        pending: 'Menunggu Pembayaran',
        paid: 'Lunas',
        expired: 'Kedaluwarsa',
        failed: 'Gagal',
      };
      return map[this.order?.payment_status] || this.order?.payment_status;
    },
    orderStatusLabel() {
      const map = {
        pending: 'Menunggu',
        processed: 'Diproses',
        shipped: 'Dikirim',
        completed: 'Selesai',
        cancelled: 'Dibatalkan',
      };
      return map[this.order?.status] || this.order?.status;
    },
    paymentBadgeClass() {
      const s = this.order?.payment_status;
      return {
        'badge-warning': s === 'pending',
        'badge-success': s === 'paid',
        'badge-danger': s === 'failed' || s === 'expired',
      };
    },
    orderBadgeClass() {
      const s = this.order?.status;
      return {
        'badge-warning': s === 'pending',
        'badge-info': s === 'processed',
        'badge-primary': s === 'shipped',
        'badge-success': s === 'completed',
        'badge-danger': s === 'cancelled',
      };
    },
    paymentMethodLabel() {
      const m = this.order?.payment_method;
      if (!m) return '';
      if (m === 'transfer_manual') return 'Transfer Bank Manual';
      if (m === 'cod') return 'Bayar di Tempat (COD)';
      if (this.tripayPaymentName) return this.tripayPaymentName;
      if (m.startsWith('tripay_')) return m.replace('tripay_', '').toUpperCase();
      return m;
    },
    expiredLabel() {
      const exp = this.order?.payment_expired_at || (this.tripay?.expired_time ? this.tripay.expired_time * 1000 : null);
      if (!exp) return '';
      const d = new Date(exp);
      if (Number.isNaN(d.getTime())) return '';
      return 'Batas waktu: ' + d.toLocaleString('id-ID');
    },
    waBuktiLink() {
      if (!this.ownerPhone) return '';
      const n = this.ownerPhone.replace(/^0/, '62').replace(/[^\d]/g, '');
      const text = encodeURIComponent(
        'Halo, saya kirim bukti transfer untuk order ' + (this.order.invoice_number || this.order.order_id)
      );
      return `https://wa.me/${n}?text=${text}`;
    },
  },
  async mounted() {
    this.orderId = this.$route.params.orderId;
    if (!this.effectiveSlug && this.onCustomDomain) {
      await this.$store.dispatch('public_shop/public_get_shop_by_domain');
    }
    await this.$store.dispatch('public_shop/public_get_shop', this.effectiveSlug);
    await this.load();
    this.pollTimer = setInterval(this.pollLoad, 5000);
  },
  beforeDestroy() {
    if (this.pollTimer) clearInterval(this.pollTimer);
  },
  methods: {
    async load() {
      try {
        const r = await this.$store.dispatch('public_shop/public_get_order', this.orderId);
        const data = r.data.data;
        this.order = data.order;
        this.items = data.items || [];
        this.shopInfo = data.shop || null;
        this.tripay = data.tripay || this.tripay;
        this.digitalAccessItems = data.digital_access_items || [];
        this.hasDigitalAccess = !!data.has_digital_access;
        applyShopTheme(pickShopTheme(this.shopInfo, this.$store.state.public_shop.shop));
        if (this.order && (this.order.payment_status === 'paid' || this.order.status === 'paid')) {
          trackPurchase(this.order, this.items);
        }
      } catch (e) {
        this.order = null;
      } finally {
        this.loading = false;
      }
    },
    async pollLoad() {
      if (this.isPaid || this.order?.status === 'cancelled') {
        if (this.pollTimer) {
          clearInterval(this.pollTimer);
          this.pollTimer = null;
        }
        return;
      }
      this.polling = true;
      const prev = this.order?.payment_status;
      try {
        await this.load();
        const now = this.order?.payment_status;
        if (prev === 'pending' && now === 'paid' && !this.wasPaidAnnounced) {
          this.wasPaidAnnounced = true;
          const msg = this.hasDigitalAccess
            ? 'Pembayaran dikonfirmasi! Akses produk digital Anda sudah tersedia di bawah.'
            : 'Pembayaran dikonfirmasi! Pesanan akan segera diproses.';
          this.$toast.success(msg);
          trackPurchase(this.order, this.items);
          if (this.pollTimer) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
          }
        }
      } finally {
        this.polling = false;
      }
    },
    async getPaymentLink() {
      if (!this.order?.payment_method) {
        this.$toast.error('Metode pembayaran tidak tersedia');
        return;
      }
      this.fetchingPayment = true;
      try {
        const r = await this.$store.dispatch('public_shop/public_get_payment_link', {
          orderId: this.orderId,
          method: this.order.payment_method,
        });
        const d = r.data.data;
        if (d.payment_url) {
          window.open(d.payment_url, '_blank');
        }
        if (d.pay_code || d.payment_name) {
          this.tripay = {
            ...(this.tripay || {}),
            pay_code: d.pay_code || this.tripay?.pay_code,
            payment_name: d.payment_name || this.tripay?.payment_name,
            checkout_url: d.payment_url,
          };
        }
        await this.load();
      } catch (e) {
        this.$toast.error(e.response?.data?.message || 'Gagal generate link bayar');
      } finally {
        this.fetchingPayment = false;
      }
    },
  },
};
</script>

<style scoped>
.status-hero h3 {
  font-weight: 600;
}
.access-text-box {
  background: #1f2937;
  color: #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  font-size: 13px;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
}
.digital-access-item:not(:last-child) {
  padding-bottom: 12px;
  border-bottom: 1px solid #eee;
}
</style>
